<template>
  <div class="dashboard-box">
    <h2>Currently raising:</h2>
    <div
      v-show="editable"
      class="text-muted margin-bottom-20"
      v-html="helpMessages.currentlyRaising"
    ></div>
    <div
      v-show="editable && !isCurrentRoundFormVisible"
      class="margin-bottom-30"
    >
      <button
        class="btn btn-default btn-transparent"
        @click="showCurrentRound()"
      >
        Set current round
      </button>
    </div>

    <div v-show="isCurrentRoundFormVisible">
      <div v-if="editable || startup.round_type" class="row">
        <div class="col-md-3">
          <p class="dashboard-label">
            Type
          </p>
        </div>
        <div class="col-md-9">
          <p v-show="!editable" class="dashboard-value">
            {{ startup.round_type | roundTypeLabel }}
          </p>
          <p v-show="editable" class="dashboard-value">
            <RoundTypeDropdown
              :value="startup.round_type"
              :choices="currentRoundTypeChoices"
              class="seamless overview"
              @change="
                $store.commit('startupProfile/SET_CURRENT_ROUND_TYPE', $event)
              "
            />
          </p>
        </div>
      </div>

      <div class="row">
        <div class="col-md-3">
          <p class="dashboard-label">
            <HelpTip
              title=""
              message="Size amount in €, eg. 100000"
              label="Size"
            />
          </p>
        </div>
        <div class="col-md-9">
          <p v-show="!editable" class="dashboard-value">
            {{ startup.round_size | numberToMoney }}
          </p>
          <el-popover title="Tip" placement="right">
            <div v-html="helpMessages.currentlyRaisingSize"></div>
            <input
              v-show="editable"
              slot="reference"
              class="form-control overview-input"
              placeholder="Size in EUR"
              :value="startup.round_size"
              @blur="onInputChangedForField('round_size', $event.target.value)"
            />
          </el-popover>
          <p
            v-for="error in (apiErrors.round_size || [])"
            :key="error"
            class="text-danger"
          >
            {{ error }}
          </p>
        </div>
      </div>

      <div v-show="editable || startup.already_committed" class="row">
        <div class="col-md-3">
          <p class="dashboard-label">
            <HelpTip
              title=""
              message="Already committed amount in €, eg. 100000"
              label="Already committed"
            />
          </p>
        </div>
        <div class="col-md-9">
          <p v-show="!editable" class="dashboard-value">
            {{ startup.already_committed | numberToMoney }}
          </p>
          <input
            v-show="editable"
            class="form-control overview-input"
            placeholder="Amount in EUR"
            :value="startup.already_committed"
            @blur="
              onInputChangedForField('already_committed', $event.target.value)
            "
          />
          <p
            v-for="error in (apiErrors.already_committed || [])"
            :key="error"
            class="text-danger"
          >
            {{ error }}
          </p>
        </div>
      </div>

      <div v-show="editable || startup.already_committed_investors" class="row">
        <div class="col-md-3">
          <p class="dashboard-label">
            Committed Investors
          </p>
        </div>
        <div class="col-md-9">
          <p v-show="!editable" class="dashboard-value">
            {{ startup.already_committed_investors }}
          </p>
          <input
            v-show="editable"
            :value="startup.already_committed_investors"
            class="form-control overview-input"
            @blur="
              $store.commit(
                'startupProfile/SET_CURRENT_ROUND_COMMITTED_INVESTORS',
                $event.target.value,
              )
            "
          />
          <p
            v-for="error in (apiErrors.already_committed_investors || [])"
            :key="error"
            class="text-danger"
          >
            {{ error }}
          </p>
        </div>
      </div>

      <div v-if="editable || startup.round_securities.length" class="row">
        <div class="col-md-3">
          <p class="dashboard-label">
            Security
          </p>
        </div>
        <div class="col-md-9">
          <p v-show="!editable" class="dashboard-value">
            {{ startup.round_securities | roundSecurityArrayLabels }}
          </p>
          <p
            v-if="editable && startup && startup.round_securities"
            class="dashboard-value"
          >
            <MultiCheckboxSelect
              class="is-fullwidth"
              :value="startup.round_securities"
              :choices="roundSecurityChoices"
              @change="
                $store.commit(
                  'startupProfile/SET_CURRENT_ROUND_SECURITIES',
                  $event,
                )
              "
            />
          </p>
        </div>
      </div>

      <div v-if="editable || startup.round_sources.length" class="row">
        <div class="col-md-3">
          <p class="dashboard-label">
            Source
          </p>
        </div>
        <div class="col-md-9">
          <p v-show="!editable" class="dashboard-value">
            {{ startup.round_sources | roundSourceArrayLabels }}
          </p>
          <p v-show="editable" class="dashboard-value">
            <MultiCheckboxSelect
              class="is-fullwidth"
              :value="startup.round_sources"
              :choices="currentRoundSourceChoices"
              @change="
                $store.commit(
                  'startupProfile/SET_CURRENT_ROUND_SOURCES',
                  $event,
                )
              "
            />
          </p>
        </div>
      </div>

      <div v-show="editable" class="row">
        <div class="col-sm-12">
          <button
            class="btn btn-danger btn-xs btn-transparent"
            @click="removeCurrentRound()"
          >
            Remove round
          </button>
        </div>
      </div>
    </div>
    <!-- end of current round form -->
  </div>
</template>

<script>
import helpMessages from './helpMessages'
import MultiCheckboxSelect from 'components/MultiCheckboxSelect.vue'
import RoundTypeDropdown from './RoundTypeDropdown.vue'
import { mapState } from 'vuex'
import { moneyToNumber, numberToMoney } from '@/filters'
import {
  roundTypeLabel,
  roundSourceArrayLabels,
  roundSecurityArrayLabels,
  roundSecurityChoices,
  currentRoundTypeChoices,
  currentRoundSourceChoices,
} from 'services/investment-rounds'
import HelpTip from '../../components/common/HelpTip'

export default {
  components: { HelpTip, MultiCheckboxSelect, RoundTypeDropdown },
  filters: {
    numberToMoney,
    roundTypeLabel,
    roundSourceArrayLabels,
    roundSecurityArrayLabels,
  },
  props: {
    editable: { type: Boolean, default: false },
  },
  data() {
    return {
      helpMessages,
      manuallyExpandedCurrentRound: false,
      currentRoundTypeChoices,
      currentRoundSourceChoices,
      roundSecurityChoices,
    }
  },
  computed: {
    isCurrentRoundFormVisible() {
      const startup = this.startup
      return [
        startup.round_type,
        startup.round_size,
        startup.round_securities.length > 0,
        startup.round_sources.length > 0,
        startup.already_committed_investors &&
          startup.already_committed_investors.length > 0,
        this.manuallyExpandedCurrentRound,
      ].some((o) => o)
    },
    ...mapState({
      startup: (state) => state.startupProfile.startup,
      apiErrors: (state) => state.startupProfile.errors,
    }),
  },
  methods: {
    onInputChangedForField(attr, value) {
      value = value ? value : null
      if (this.startup[attr] !== value) {
        this.$store.dispatch('startupProfile/setAttr', { attr, value })
      }
    },
    showCurrentRound() {
      this.manuallyExpandedCurrentRound = true
    },
    removeCurrentRound() {
      this.$store.commit('startupProfile/REMOVE_CURRENT_ROUND')
      this.manuallyExpandedCurrentRound = false
    },
  },
}
</script>

<style lang="scss" scoped></style>
